import { useNavigate } from '@tanstack/react-router';
import { AlarmClockOff, Ban, Receipt, Snowflake, TriangleAlert, Undo2, UserX } from 'lucide-react';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { DepositorAbandonmentRow } from '@pf/contracts';
import { useDepositorAbandonment, type MetricsRange } from '@/lib/api/ops-queries';
import { nowMs, toEpochMs } from '@/lib/clock';
import {
  formatDate,
  formatDateTimeCompact,
  formatDuration,
  formatRelative,
  msUntil,
} from '@/lib/datetime';
import {
  isPlainClick,
  readParam,
  useDebouncedParam,
  useListState,
  withBasePath,
} from '@/lib/nav/list-state';
import {
  ABANDONMENT_LIST,
  ABANDONMENT_SORTS,
  ABANDONMENT_WINDOWS,
  depositorHistoryHref,
  isSplitDependentSort,
} from '@/lib/nav/ops-lists';
import { ALL_WEBS, useWebScope } from '@/lib/scope/web-scope';
import { TONE_CLASSES } from '@/lib/states';
import { cn } from '@/lib/utils';
import { CopyButton } from '@/components/domain/copy-button';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader } from '@/components/layout/page';
import { Pager } from '@/components/layout/pager';
import {
  CustomRangePicker,
  readCustomRange,
  windowButtonClasses,
} from '@/components/layout/range-picker';
import { SortControl } from '@/components/layout/sort-control';
import { Button } from '@/components/ui/button';
import { Callout, EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import { Input, Label, Tooltip } from '@/components/ui/primitives';

/** A sort key from the table header or the mobile control, ignored unless this page offers it. */
const readSort = readParam.oneOf(ABANDONMENT_SORTS);

/** The i18n key for each window preset, in the order the button group renders them. */
const WINDOW_LABEL_KEYS: Readonly<Record<number, string>> = {
  24: 'abandonment.window.day',
  168: 'abandonment.window.week',
  720: 'abandonment.window.month',
};

/**
 * "24 hours" / "7 days" / "9.8 hours" — the length of a window, spoken.
 *
 * The one-decimal rounding is not decoration. Since the window can now be an absolute range an
 * operator typed, its length is no longer one of three round numbers: a range from 08:15 to 18:00
 * is 9.75 h, and an unrounded `9.75` would print with whatever float arithmetic left behind. The
 * days form stays reserved for exact multiples, because "1.3 days" is harder to picture than
 * "31.2 hours" — and in practice a custom range is named by its *bounds* rather than by its length
 * anyway (see the window label below), so this branch is the presets' and stays their shape.
 */
function windowText(t: TFunction, hours: number): string {
  if (hours > 24 && hours % 24 === 0) return t('abandonment.window.days', { count: hours / 24 });
  return t('abandonment.window.hours', { count: Math.round(hours * 10) / 10 });
}

/** A proportion in `[0, 1]` as a percentage string. One decimal: the ranking turns on tenths. */
function percent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * "Show me what this account actually did" — the drill-through into History.
 *
 * This page ranks *accounts*, and a rank is an accusation. The number that produced it is a
 * summary, and the operator about to restrict or block someone on the strength of it needs the
 * rows underneath: were these thirty timeouts one bad afternoon or three months of parking slots,
 * did the completed deposits all come from one bank, is there a support ticket that explains it.
 * The leaderboard cannot answer any of that and should not try — History already does, and now the
 * row links straight into it, filtered to this depositor (`ops-lists.ts`'s `depositorHistoryHref`).
 *
 * **It sits beside the copy button rather than replacing it**, because the two answer genuinely
 * different needs and neither substitutes for the other. The link is for the operator who is
 * staying inside this console and wants the evidence; the copy is for the one who is *leaving* it,
 * carrying the reference into the participating website's own back office or into a message to
 * their risk team — a destination this console cannot navigate to and never will. Collapsing the
 * pair into one affordance would break whichever journey lost.
 *
 * A real `<a href>` with the click handler taking over only a plain click, exactly as `BackLink`
 * does: an operator investigating an account very often wants it in a *second tab*, beside the
 * leaderboard they are working down, and a `<button onClick={navigate}>` cannot be middle-clicked.
 *
 * **What the label promises is deliberately narrower than what the operator is thinking.** History
 * searches the whole platform — `GET /ops/v1/transactions` has no tenant filter and an operator
 * account has no participant to scope by — while an account reference is only unique inside the
 * site that minted it. So a reference two participants both happen to use lands on both sites'
 * rows, and this link cannot promise otherwise. It therefore says it *finds deposits matching this
 * account reference* rather than "this depositor's deposits": the honest phrasing costs a few
 * characters, and the flattering one would let an operator who arrived from one specific row read
 * somebody else's history as their evidence. History renders the participant on every row, so the
 * ambiguity stays visible once they are there; naming it here is what makes them look.
 */
function DepositorHistoryLink({
  accountRef,
  label,
}: {
  /** The depositor whose deposits the link lands on. */
  accountRef: string;
  /** The link's accessible name — it has no visible text, only a glyph. */
  label: string;
}): ReactNode {
  const navigate = useNavigate();
  const href = depositorHistoryHref(accountRef);
  return (
    <Button asChild variant="ghost" size="icon" className="size-5 shrink-0 [&_svg]:size-3">
      <a
        href={withBasePath(href)}
        aria-label={label}
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          void navigate({ href });
        }}
      >
        <Receipt aria-hidden="true" />
      </a>
    </Button>
  );
}

/**
 * One of the two abandonment behaviours, as a count.
 *
 * The whole visual argument of this page is in this component. Both behaviours end the same way —
 * the slot goes back to the pool and the deposit never happens — so nothing in the *data* makes
 * one look worse than the other. The severity has to come from the presentation, and it comes from
 * the shared tone vocabulary rather than from colours invented here: `attention` for a deliberate
 * release, `bad` for a TTL reclaim. An operator who has read any other screen in this console
 * already knows which of those two means "worse".
 *
 * A zero is rendered as a plain muted digit rather than as an empty-but-toned badge. That is what
 * makes the table scannable: every coloured pill on screen is a real count, so the eye can find
 * the accounts worth acting on without reading a single number.
 */
function BehaviourCount({
  count,
  tone,
  icon,
  tooltip,
}: {
  /** How many reservations ended this way. */
  count: number;
  /** Which severity this behaviour carries. */
  tone: 'attention' | 'bad';
  /** The behaviour's glyph. */
  icon: ReactNode;
  /** What this behaviour means, plus the split caveat when the window predates the cutover. */
  tooltip: string;
}): ReactNode {
  if (count === 0) {
    return <span className="tnum text-xs text-muted-foreground">0</span>;
  }
  return (
    <Tooltip content={tooltip}>
      <span
        className={cn(
          'tnum inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold',
          TONE_CLASSES[tone].badge,
        )}
      >
        {icon}
        {count}
      </span>
    </Tooltip>
  );
}

/**
 * The account's standing: its enforcement tier, and any cooldown still running.
 *
 * `STANDARD` is deliberately not a badge. Almost every account is standard, and a table where
 * every row carries a chip saying so is a table where the two rows that say something else are
 * invisible — which would defeat the point of surfacing the column at all.
 *
 * Neither of these is affected by the cancelled/timed-out reporting caveat below.
 * `depositor_reputation`'s counters were never touched by the domain change that introduced the
 * split, so tier and cooldown are accurate for all of history, however far back the window reaches.
 */
function Standing({
  row,
  at,
  t,
}: {
  /** The row being described. */
  row: DepositorAbandonmentRow;
  /** The instant "now" resolves to, so every row in one render agrees. */
  at: number;
  /** Translator, passed in because this renders inside a column definition. */
  t: TFunction;
}): ReactNode {
  const cooldownMs = row.cooldown_until === null ? 0 : msUntil(row.cooldown_until, at);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {row.tier === 'BLOCKED' ? (
        <Tooltip content={t('abandonment.tier.blockedTooltip')}>
          <span
            className={cn(
              'inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold',
              TONE_CLASSES.bad.badge,
            )}
          >
            <Ban className="size-3" aria-hidden="true" />
            {t('abandonment.tier.BLOCKED')}
          </span>
        </Tooltip>
      ) : row.tier === 'RESTRICTED' ? (
        <Tooltip content={t('abandonment.tier.restrictedTooltip')}>
          <span
            className={cn(
              'inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold',
              TONE_CLASSES.attention.badge,
            )}
          >
            <UserX className="size-3" aria-hidden="true" />
            {t('abandonment.tier.RESTRICTED')}
          </span>
        </Tooltip>
      ) : (
        <span className="text-2xs text-muted-foreground">{t('abandonment.tier.STANDARD')}</span>
      )}

      {cooldownMs > 0 ? (
        <Tooltip
          content={t('abandonment.cooldown.tooltip', {
            level: row.cooldown_level,
            duration: formatDuration(cooldownMs),
          })}
        >
          <span
            className={cn(
              'tnum inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium',
              TONE_CLASSES.flight.badge,
            )}
          >
            <Snowflake className="size-3" aria-hidden="true" />
            {formatDuration(cooldownMs)}
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}

/**
 * A column header carrying the "this number is partly misattributed" mark.
 *
 * The mark lives on the *header* rather than on each cell for a reason that is specific to how
 * `DataTable` renders: at `md` and up the header row is `sticky`, so the mark stays on screen for
 * as long as the column it qualifies does — and below `md`, where the table becomes cards, the
 * header text is repeated as every card's `<dt>`, so the mark is repeated with it. One placement,
 * visible on every row in both renderings.
 *
 * The icon is decorative and the sentence behind it is `sr-only`, because a bare warning triangle
 * with no accessible name is exactly the kind of "you had to be there" marking this is meant to
 * replace.
 */
function CaveatHeader({ label, caveat }: { label: string; caveat: string }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <TriangleAlert className={cn('size-3', TONE_CLASSES.attention.text)} aria-hidden="true" />
      <span className="sr-only">{caveat}</span>
    </span>
  );
}

/**
 * The same mark as {@link CaveatHeader}, attached to the chosen **order** rather than to a column.
 *
 * Why a second placement at all: the column marks tell an operator that two *numbers* are partly
 * misattributed, which is true and incomplete. Sorting by one of those numbers makes the *ranking*
 * wrong — the row at the top may not be the worst account — and this page's whole output is a
 * decision taken from the top of the table. The mark therefore has to appear where the ordering
 * was chosen, not only where its inputs are displayed.
 *
 * And the column marks cannot cover it even in principle: `TIMEOUT_RATE` is a sort with no column
 * on this table (see `ABANDONMENT_SPLIT_DEPENDENT_SORTS`), so an operator who reaches it from the
 * mobile control or from a pasted URL previously got a silently wrong order with nothing on screen
 * saying so. That is exactly why this renders at every breakpoint while the `SortControl` beside it
 * is `md:hidden`.
 *
 * Same vocabulary as the header mark rather than a new one — the same `TriangleAlert` in the same
 * `attention` tone — because an operator should read it as "that mark again, about my sort", not as
 * a second unfamiliar warning. What differs is that the sentence is *visible*: beside a column
 * label the glyph has a label to qualify, so `sr-only` prose is enough, whereas here there is
 * nothing for a bare triangle to attach to and a sighted operator would learn nothing from it.
 *
 * The element is mounted whether or not there is anything to say, and carries `aria-live` for the
 * same reason: switching the select from `Abandonment score` to `Timeout rate` is precisely the
 * moment the warning becomes true, and a region inserted at that moment is not reliably announced.
 * Empty it is a flex box with no children, so it collapses to zero height and costs no layout.
 */
function CaveatSortNote({ caveat }: { caveat: string | null }): ReactNode {
  return (
    <p
      aria-live="polite"
      className={cn(
        'flex max-w-xs items-start gap-1 text-2xs leading-relaxed',
        TONE_CLASSES.attention.text,
        caveat !== null && 'mt-1.5',
      )}
    >
      {caveat === null ? null : (
        <>
          <TriangleAlert className="mt-px size-3 shrink-0" aria-hidden="true" />
          {caveat}
        </>
      )}
    </p>
  );
}

/**
 * Depositor abandonment — who reserves a slot and then does not follow through.
 *
 * This is a table and not a chart, and that is the whole design. A chart would answer "is
 * abandonment up?", which the dashboard's release-rate tile already answers. The question that
 * brings someone *here* is the next one — **which accounts, by name, and what do I do about
 * them?** — and the answer to that has to be a list of identifiers an operator can copy, not a
 * shape. Everything below follows from that: no chart, sortable columns, a threshold filter, and
 * the two facts that decide whether to act (tier and cooldown) on every row.
 *
 * ## Two behaviours, one outcome, different severities
 *
 * A reservation leaves `RESERVED` without a payment in exactly two ways, and `@pf/domain`'s
 * `TransactionExpiryCause` is emphatic that they are the same transition — same effects, same
 * penalty — because the money question is identical. The comment there also says the split *is*
 * one operators want, as a reporting concern, and this page is that report:
 *
 * - **Released** (`PARTICIPANT_RELEASE`) — the depositor let the slot go. Milder: it went back to
 *   the pool within seconds and the next withdrawer in line barely waited.
 * - **Timed out** (`RESERVATION_TTL`) — nobody came back and the timer reclaimed it. Worse by a
 *   lot: a withdrawer was held out of the pool for the entire TTL in exchange for nothing.
 *
 * The two get separate columns rather than one combined "abandoned" number precisely so an
 * operator can tell a customer who keeps changing their mind from one who is parking slots.
 *
 * ## The split has a start date, and the page has to say so
 *
 * Both behaviours used to write the identical event. Only since the domain change that added
 * `cause` can they be told apart, and the history before it cannot be back-filled — the signal was
 * never recorded, so every older deliberate release is filed as a timeout, permanently.
 *
 * That lands on the most severe column on the screen, on a page whose output is a decision to
 * restrict or block a real person, so it is stated in two places rather than one: a Callout above
 * the table whenever the selected window reaches back past `split_reliable_from`, and a mark on
 * the two affected column headers, which stay visible while an operator scrolls (see
 * {@link CaveatHeader}). Nothing is hidden, zeroed or collapsed back into a combined total — the
 * data is still the best available and the operator asked for the split; it just needs its caveat
 * attached to the number rather than to the page.
 *
 * Worth stating precisely, because it changes what an operator should trust: the *total* is
 * unaffected. `abandon_rate`, `abandon_score` and the ranking are computed from
 * `cancelled + timed_out`, and that sum was always recorded correctly. Only the division of that
 * sum into two columns is unreliable before the cutover. So a caveated window still ranks
 * correctly **by the default order**; it just cannot tell you *which kind* of abandonment earned
 * the rank.
 *
 * ### …which stops being true the moment the operator sorts by the split
 *
 * That last paragraph is a statement about `ABANDON_SCORE` and `ABANDON_RATE`, and for a while the
 * page quietly treated it as a statement about the screen. It is not. Three of the seven orders
 * this list offers — `TIMEOUT_RATE`, `TIMED_OUT` and `CANCELLED` — rank *by the misattributed half
 * itself*, so over a caveated window they order the table on numbers that are inflated or deflated
 * by an amount that differs per account. Two depositors who behaved identically can land either way
 * round depending only on how much of their history predates the cutover. The reasoning, and why
 * the other four are deliberately left alone, lives on `ABANDONMENT_SPLIT_DEPENDENT_SORTS`.
 *
 * A caveat on the columns does not cover that, for two reasons. It marks the *inputs* rather than
 * the ordering an operator actually acts on — the top of this table is where someone decides to
 * restrict a real person — and `TIMEOUT_RATE` has no column on this table at all, so the one order
 * most obviously built on the unreliable half had no header available to carry a mark. It is
 * nonetheless reachable, from the mobile `SortControl` and from any pasted or hand-edited URL.
 *
 * So when both facts hold — the window reaches back past the cutover *and* the active order is one
 * of the three — the page says so twice more: beside the sort control, where the choice was made
 * (see {@link CaveatSortNote}), and as an extra line in the same Callout, so the banner explaining
 * what this window means does not stop short of the thing most likely to mislead. The sort is not
 * disabled, hidden or overridden, exactly as the columns are not: the operator asked for this
 * order, it is still the best available answer, and the fix for a caveated number has never been to
 * take it away.
 *
 * ## Why the default sort is the score and not the rate
 *
 * Sorting by raw abandon rate puts every account that reserved once and walked away at the top,
 * all at a perfect 100 %, and buries the account that abandoned sixty times out of a hundred
 * underneath them. That is worse than useless — it is a leaderboard of noise. So the default order
 * is a Wilson 95 % lower bound: *given this much evidence, how bad is this account at least?* A
 * three-reservation sample cannot support a strong claim and sinks on its own, without a hard
 * minimum-sample cutoff having to make that judgement by fiat.
 *
 * The consequence is that the page will sometimes show 100 % ranked below 60 %, which reads as a
 * broken sort unless the page says otherwise. It says otherwise in two places, both cheap: a
 * standing note beside the row count, and a per-cell tooltip that names the sample size the score
 * was computed over. The raw rate stays visible in its own sortable column, so an operator who
 * wants the naive order can have it and see for themselves what changes.
 *
 * ## Deliberately not money-weighted
 *
 * There is no amount, volume or value column anywhere on this screen, and that is a product
 * decision rather than an omission. The harm being measured is *occupancy* — a withdrawer held out
 * of the pool — and that harm is the same whether the abandoned reservation was for 300 THB or
 * 30 000. Ranking by value would quietly turn an abuse-control screen into a VIP screen.
 *
 * ## Tier and cooldown
 *
 * Both have existed in `depositor_reputation` since migration 0008 and neither has ever been
 * visible to an operator. They are the reason a support question like "why can this customer not
 * reserve?" had no answer in this console. Surfacing them here is half of what this page is for.
 *
 * ## Any window, not only the three presets
 *
 * The window group offers 1d/1w/1m *and* the absolute range picker the dashboard uses
 * (`components/layout/range-picker.tsx`). The presets are the common case and stay first, but they
 * cannot express the question this screen is most often opened with during an incident — "who was
 * holding slots between 14:00 and 18:00 on the day the pool ran dry?" — and rounding that up to
 * "the last day" both dilutes the ranking with unrelated behaviour and quietly answers a different
 * question. The endpoint has always accepted `from`/`to` (it shares `resolveRange` with `/metrics`);
 * this screen simply had no way to ask.
 *
 * Two consequences are worth stating because they are easy to get wrong:
 *
 *  - **The range lives in the URL**, with every other filter, so the promise below holds for it
 *    too. A window an operator picked because an incident had those bounds is precisely the
 *    selection worth pasting into a channel, and one held in component state would be the only
 *    part of this view a colleague could not receive.
 *  - **It makes the split caveat far easier to trigger**, which is a feature rather than a hazard.
 *    Reaching back past the cancelled/timed-out cutover used to require switching to 1w or 1m; now
 *    it takes typing last month's date, which is exactly what an operator investigating an old
 *    complaint does first. The caveat is computed off the *response's* bounds, so it covers the new
 *    case without knowing about it — see the comment where `splitCaveat` is derived.
 *
 * ## Where a row leads
 *
 * A rank is a summary, and this page's output is a decision about a person, so every row offers
 * the evidence behind its number: a link into History filtered to that depositor, beside the copy
 * button that carries the reference out of the console entirely. {@link DepositorHistoryLink}
 * explains why both.
 *
 * The filter, sort, window, range and page live in the URL (`@/lib/nav/list-state`), so a view
 * worth escalating is a link someone can paste into an incident channel.
 */
export function AbandonmentPage(): ReactNode {
  const { t } = useTranslation();
  const { scope } = useWebScope();
  const view = useListState(ABANDONMENT_LIST);
  const { window: windowHours, min, sort, page, limit } = view.state;
  // Every column on this page is "worst first" except the timestamp, which is "most recent
  // first" — and both of those are DESC, so one default covers the lot.
  const dir = view.state.dir ?? 'DESC';
  // One clock read for the whole render, so every row's cooldown, the "last seen" column and the
  // range validation below all agree about when "now" is.
  const at = nowMs();

  // What window is on screen: the absolute range in the URL if there is a usable one, else the
  // preset. The precedence is one-way and deliberate — `window` is left in the address bar while a
  // range is in force, so *clearing* the range drops the operator back onto the preset they were
  // last reading rather than onto a default, and a link shared mid-investigation still carries
  // both. `readCustomRange` is the picker's own validator, which is what makes a hand-edited,
  // truncated or long-stale pair degrade to that preset instead of becoming a request the API
  // would refuse.
  const range: MetricsRange = readCustomRange(view.state.from, view.state.to, at) ?? {
    kind: 'preset',
    hours: windowHours,
  };

  const board = useDepositorAbandonment({
    range,
    minReservations: min,
    sort,
    dir,
    page,
    limit,
  });

  // The threshold is a number the operator types, so it debounces like a search box: without this
  // "10" is two requests, the first for everything above 1.
  const commitMin = useCallback(
    (next: string) => {
      const parsed = Number.parseInt(next, 10);
      view.set({ min: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0, page: 0 });
    },
    [view],
  );
  const [minDraft, setMinDraft] = useDebouncedParam(String(min), commitMin);

  // Header click: the same column flips direction, a new column starts at its natural order —
  // which, with no `dir` in the URL, is the worst-first derivation above.
  const sortBy = (key: string): void => {
    const next = readSort(key);
    if (next === undefined) return;
    view.set(
      next === sort
        ? { dir: dir === 'ASC' ? 'DESC' : 'ASC', page: 0 }
        : { sort: next, dir: undefined, page: 0 },
    );
  };

  const rows = board.data?.items ?? [];
  const total = board.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  // The window's name for the description sentence. Read off the response's own bounds so the
  // sentence and the numbers under it describe the same span — except on the frame where
  // `keepPreviousData` is still holding the outgoing window, where the payload describes the
  // *previous* selection and the label falls back to what was just asked for.
  //
  // A custom range is named by its *bounds*, in the operator's local zone, rather than by its
  // length: "the last 9.8 hours" would make someone who typed 08:15–18:00 do the arithmetic the
  // picker just did for them, and — worse on this screen than on the dashboard — an absolute range
  // is usually chosen because an *incident* had those bounds, so the bounds are the part worth
  // repeating back.
  const settled = board.isPlaceholderData ? null : board.data;
  const reportedHours =
    settled === null || settled === undefined
      ? windowHours
      : (toEpochMs(settled.range_end) - toEpochMs(settled.range_start)) / 3_600_000;
  const windowLabel =
    range.kind === 'custom'
      ? t('abandonment.window.range', {
          from: formatDateTimeCompact(settled?.range_start ?? range.from),
          to: formatDateTimeCompact(settled?.range_end ?? range.to),
        })
      : t('abandonment.window.last', { duration: windowText(t, reportedHours) });

  // Does this window reach back past the instant from which the cancelled/timed-out split can be
  // trusted? `undefined` means the field has not shipped yet and `null` means no cutover applies;
  // in both cases there is nothing truthful to warn about, so the caveat stays off.
  //
  // Note what this compares: the *response's* `range_start` against the *response's*
  // `split_reliable_from`, never the selection against a locally computed instant. That is what
  // makes the caveat correct for a custom range without knowing that custom ranges exist — the
  // server resolves the window (clamping a `to` inside the skew tolerance, defaulting an absent
  // one) and reports the bounds it actually measured over, so the comparison is between two facts
  // from the same payload. It also matters more now than it did: three presets could only reach
  // past the cutover in two of their three positions, whereas an absolute range makes reaching
  // back past it as easy as typing last month's date, which is exactly what an operator
  // investigating an old complaint does first.
  const splitFrom = settled?.split_reliable_from;
  const splitCaveat =
    splitFrom !== undefined &&
    splitFrom !== null &&
    settled !== null &&
    settled !== undefined &&
    toEpochMs(settled.range_start) < toEpochMs(splitFrom);
  const splitFromDate = splitFrom === undefined || splitFrom === null ? '' : formatDate(splitFrom);
  /** Appended to both behaviour tooltips while the caveat is live, so a row carries it too. */
  const cellCaveat = splitCaveat
    ? ` ${t('abandonment.splitCaveat.cellSuffix', { date: splitFromDate })}`
    : '';

  // Is the *order* on screen affected, and not merely the two columns? Two independent facts have
  // to hold, and the AND is the whole point: an affected sort over a window that starts after the
  // cutover ranks perfectly well, and a caveated window sorted by score or rate ranks perfectly
  // well too. Warning about either alone would be crying wolf, and a caveat an operator learns to
  // scroll past is worse than none — it launders the case where the ranking really is wrong.
  const sortCaveat = splitCaveat && isSplitDependentSort(sort);
  /** The active order's own label, so both caveat sentences name it the way the control does. */
  const sortLabel = t(`abandonment.sortOptions.${sort}`);

  const columns: Column<DepositorAbandonmentRow>[] = [
    {
      id: 'depositor',
      header: t('abandonment.columns.depositor'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0 space-y-1">
          {/* The website's display handle when it has sent one — the name an operator quotes —
              above the ref, which stays visible because it is the identity History matches and
              the website's risk team keys on. */}
          {row.account_display != null ? (
            <p className="truncate text-xs font-medium">{row.account_display}</p>
          ) : null}
          <div className="flex min-w-0 items-center gap-1">
            <span className="truncate font-mono text-xs font-medium">{row.account_ref}</span>
            {/* Two affordances on one reference, because "what did this account do?" and "who do I
                tell about this account?" are different questions and this page is where both get
                asked. The link goes to the evidence inside this console; the copy carries the
                reference *out* of it — into the participating website's own back office, or into a
                message to their risk team — which is a destination no link can reach. The
                reference is passed whole, because History matches it by whole value. See
                {@link DepositorHistoryLink}. */}
            <DepositorHistoryLink
              accountRef={row.account_ref}
              label={t('abandonment.viewTransactions', { account: row.account_ref })}
            />
            <CopyButton
              value={row.account_ref}
              label={t('abandonment.copyAccountRef')}
              className="size-5 shrink-0 [&_svg]:size-3"
            />
          </div>
          <p className="truncate text-2xs text-muted-foreground">{row.participant}</p>
        </div>
      ),
    },
    {
      id: 'score',
      header: t('abandonment.columns.score'),
      align: 'right',
      priority: 'primary',
      sortKey: 'ABANDON_SCORE',
      cell: (row) => (
        <Tooltip
          content={t('abandonment.scoreTooltip', {
            count: row.reservations_total,
            rate: percent(row.abandon_rate),
          })}
        >
          <span className="tnum cursor-help text-sm font-semibold">
            {percent(row.abandon_score)}
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'rate',
      header: t('abandonment.columns.rate'),
      align: 'right',
      priority: 'secondary',
      sortKey: 'ABANDON_RATE',
      cell: (row) => (
        <span className="tnum text-xs text-muted-foreground">{percent(row.abandon_rate)}</span>
      ),
    },
    {
      id: 'cancelled',
      header: splitCaveat ? (
        <CaveatHeader
          label={t('abandonment.columns.cancelled')}
          caveat={t('abandonment.splitCaveat.columnMark', { date: splitFromDate })}
        />
      ) : (
        t('abandonment.columns.cancelled')
      ),
      align: 'right',
      priority: 'secondary',
      sortKey: 'CANCELLED',
      cell: (row) => (
        <BehaviourCount
          count={row.cancelled}
          tone="attention"
          icon={<Undo2 className="size-3" aria-hidden="true" />}
          tooltip={`${t('abandonment.behaviour.cancelledTooltip')}${cellCaveat}`}
        />
      ),
    },
    {
      id: 'timedOut',
      header: splitCaveat ? (
        <CaveatHeader
          label={t('abandonment.columns.timedOut')}
          caveat={t('abandonment.splitCaveat.columnMark', { date: splitFromDate })}
        />
      ) : (
        t('abandonment.columns.timedOut')
      ),
      align: 'right',
      priority: 'secondary',
      sortKey: 'TIMED_OUT',
      cell: (row) => (
        <BehaviourCount
          count={row.timed_out}
          tone="bad"
          icon={<AlarmClockOff className="size-3" aria-hidden="true" />}
          tooltip={`${t('abandonment.behaviour.timedOutTooltip')}${cellCaveat}`}
        />
      ),
    },
    {
      id: 'reservations',
      header: t('abandonment.columns.reservations'),
      align: 'right',
      priority: 'secondary',
      sortKey: 'RESERVATIONS',
      cell: (row) => (
        <div className="space-y-0.5">
          <p className="tnum text-xs font-medium">{row.reservations_total}</p>
          <p className="tnum text-2xs text-muted-foreground">
            {t('abandonment.completedOf', { count: row.completed })}
          </p>
        </div>
      ),
    },
    {
      id: 'standing',
      header: t('abandonment.columns.standing'),
      priority: 'secondary',
      cell: (row) => <Standing row={row} at={at} t={t} />,
    },
    {
      id: 'fraud',
      header: t('abandonment.columns.fraudRejects'),
      align: 'right',
      priority: 'detail',
      cell: (row) =>
        row.fraud_rejects === 0 ? (
          <span className="tnum text-xs text-muted-foreground">0</span>
        ) : (
          <Tooltip content={t('abandonment.fraudTooltip')}>
            <span className={cn('tnum cursor-help text-xs font-semibold', TONE_CLASSES.bad.text)}>
              {row.fraud_rejects}
            </span>
          </Tooltip>
        ),
    },
    {
      id: 'lastSeen',
      header: t('abandonment.columns.lastSeen'),
      priority: 'detail',
      sortKey: 'LAST_SEEN',
      cell: (row) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatRelative(row.last_seen_at, at)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('abandonment.title')}
        description={t('abandonment.description', { window: windowLabel })}
        actions={
          <div
            role="group"
            aria-label={t('abandonment.window.label')}
            className="flex items-center gap-1.5"
          >
            {ABANDONMENT_WINDOWS.map((candidate) => {
              // A preset is only *pressed* when it is the window actually in force. While a custom
              // range is set none of them is, even though `window` still holds one — the button
              // group would otherwise claim to be showing a day while the table showed an
              // afternoon in March.
              const pressed = range.kind === 'preset' && windowHours === candidate;
              return (
                <button
                  key={candidate}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => {
                    // Choosing a preset drops the range, rather than leaving it in the URL to
                    // out-rank the click that was just made.
                    view.set({ window: candidate, from: '', to: '', page: 0 });
                  }}
                  className={windowButtonClasses(pressed)}
                >
                  {t(WINDOW_LABEL_KEYS[candidate] ?? 'abandonment.window.day')}
                </button>
              );
            })}
            {/* The same picker the dashboard uses, so a window means the same thing on both
                screens. Its result goes into the URL with every other filter — the sentence in
                this page's docs about a view being pasteable into an incident channel is only
                true if the *range* travels with it, and an absolute range is the one selection an
                operator would most want to hand to a colleague verbatim. */}
            <CustomRangePicker
              range={range}
              onApply={(next) => {
                if (next.kind !== 'custom') return;
                view.set({ from: next.from, to: next.to, page: 0 });
              }}
              onClear={() => {
                view.set({ from: '', to: '', page: 0 });
              }}
            />
          </div>
        }
      />

      {/* Above the split caveat on purpose: this leaderboard ranks and restricts real accounts, so
          which population it is ranking has to be the first thing an operator reads, before
          whatever the window's own caveat says about it. */}
      <WebScopeScopedNotice />

      {/* Not dismissible, and above everything an operator would act on. A window that reaches
          back past the cutover is not a degraded view to be acknowledged once and forgotten — it
          is a standing property of what is on screen, and it changes what the two columns mean. */}
      {splitCaveat ? (
        <Callout tone="warning" title={t('abandonment.splitCaveat.title', { date: splitFromDate })}>
          <p>{t('abandonment.splitCaveat.body', { date: splitFromDate })}</p>
          {/* The banner used to stop at "the two columns are wrong", which left the operator to
              infer the part that actually costs someone their account: that ordering *by* one of
              those columns ranks the table on the misattributed numbers. Said here as well as
              beside the control because this is the paragraph an operator reads to find out what
              the selected window means, and it would be a strange place to omit the consequence
              with the sharpest edge. Conditional, so an unaffected order never sees it. */}
          {sortCaveat ? (
            <p className="mt-1.5 font-medium">
              {t('abandonment.splitCaveat.bodySort', { sort: sortLabel, date: splitFromDate })}
            </p>
          ) : null}
        </Callout>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40 space-y-1.5">
          <Label htmlFor="abandonment-min">{t('abandonment.minLabel')}</Label>
          <Input
            id="abandonment-min"
            inputMode="numeric"
            className="tnum"
            value={minDraft}
            onChange={(event) => {
              setMinDraft(event.target.value.replace(/\D/g, ''));
            }}
          />
        </div>
        <p className="max-w-md pb-2 text-2xs leading-relaxed text-muted-foreground">
          {t('abandonment.minHint')}
        </p>
        {/* The sort control with its caveat mark stacked underneath it, sharing one right-aligned
            column so the mark reads as belonging to the control rather than floating in the filter
            row. `ml-auto` moved here from the control itself because the two now travel together —
            and note the asymmetry that is deliberate: the control is `md:hidden` (the desktop table
            sorts from its headers) while the mark is not, because on desktop a `TIMEOUT_RATE` sort
            has no header of its own to appear on. */}
        <div className="ml-auto flex flex-col items-end">
          <SortControl
            id="abandonment-sort"
            className="md:hidden"
            label={t('abandonment.sortLabel')}
            options={ABANDONMENT_SORTS.map((value) => ({
              value,
              label: t(`abandonment.sortOptions.${value}`),
            }))}
            value={sort}
            dir={dir}
            // Changing the column here keeps the direction the operator chose beside it.
            onValueChange={(value) => {
              const next = readSort(value);
              if (next === undefined) return;
              view.set({ sort: next, dir, page: 0 });
            }}
            onDirChange={(value) => {
              view.set({ dir: value, page: 0 });
            }}
          />
          <CaveatSortNote
            caveat={
              sortCaveat
                ? t('abandonment.splitCaveat.sortMark', { sort: sortLabel, date: splitFromDate })
                : null
            }
          />
        </div>
      </div>

      {board.isLoading ? (
        <TableSkeleton rows={8} columns={6} />
      ) : board.isError ? (
        <ErrorState
          error={board.error}
          onRetry={() => {
            void board.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<AlarmClockOff className="size-5" aria-hidden="true" />}
          title={
            min > 0
              ? t('abandonment.empty.filteredTitle')
              : scope !== ALL_WEBS
                ? t('common.webScope.emptyTitle')
                : t('abandonment.empty.clearTitle')
          }
          description={
            min > 0
              ? t('abandonment.empty.filteredDescription', { min, window: windowLabel })
              : // The threshold filter already explains itself when it is the reason the table is
                // empty; a scoped console with no threshold set needs the different explanation —
                // this site abandoned nothing in the window, not the whole pool did.
                scope !== ALL_WEBS
                ? t('common.webScope.emptyDescription', { web: scope })
                : t('abandonment.empty.clearDescription', { window: windowLabel })
          }
          action={
            min > 0 ? (
              <Button
                variant="outline"
                onClick={() => {
                  view.set({ min: 0, page: 0 });
                }}
              >
                {t('abandonment.empty.showAll')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {t('abandonment.rowCount', { count: total, min })}
            </p>
            {/* The standing explanation of the default order. Deliberately one sentence and
                deliberately always present: the inversion it explains is visible on the first
                screenful, so an explanation an operator has to go looking for is too late. */}
            <p className="max-w-xl text-2xs leading-relaxed text-muted-foreground">
              {t('abandonment.rankingNote')}
            </p>
          </div>
          <DataTable
            caption={t('abandonment.tableCaption')}
            columns={columns}
            rows={rows}
            rowKey={(row) => `${row.participant}:${row.account_ref}`}
            sort={{ key: sort, dir, onChange: sortBy }}
          />
          <Pager
            summary={t('abandonment.pageSummary', { page: page + 1, pages, total })}
            page={page}
            pages={pages}
            limit={limit}
            onPageChange={(value) => {
              view.set({ page: value });
            }}
            onLimitChange={(value) => {
              view.set({ limit: value, page: 0 });
            }}
          />
        </div>
      )}

      <p className="max-w-3xl text-2xs leading-relaxed text-muted-foreground">
        {t('abandonment.footer')}
      </p>
    </>
  );
}
